import React, { useState, useEffect } from 'react';
import { Tag, CheckCircle, AlertCircle } from 'lucide-react';

interface TagData {
  tagCategory: string;
  associatedTerms: string[];
}

interface TagSelectionProps {
  tags: TagData[];
  onSelectionComplete: (selectedTerms: string[]) => void;
  onCancel: () => void;
}

const TagSelection: React.FC<TagSelectionProps> = ({ tags, onSelectionComplete, onCancel }) => {
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const MIN_SELECTION = 5;

  useEffect(() => {
    if (selectedTerms.length >= MIN_SELECTION) {
      setError(null);
    }
  }, [selectedTerms]);

  const handleTermToggle = (term: string) => {
    setSelectedTerms(prev => {
      if (prev.includes(term)) {
        return prev.filter(t => t !== term);
      } else {
        return [...prev, term];
      }
    });
  };

  const handleComplete = () => {
    if (selectedTerms.length < MIN_SELECTION) {
      setError(`Please select at least ${MIN_SELECTION} different terms to continue.`);
      return;
    }
    onSelectionComplete(selectedTerms);
  };

  const handleCancel = () => {
    setSelectedTerms([]);
    setError(null);
    onCancel();
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Tag className="h-5 w-5 mr-2" />
            Select Tags for Domain Generation
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {selectedTerms.length}/{MIN_SELECTION} selected
            </span>
            {selectedTerms.length >= MIN_SELECTION && (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          Please select at least {MIN_SELECTION} different terms from the categories below to generate domain keywords.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      <div className="p-6 max-h-96 overflow-y-auto">
        <div className="space-y-6">
          {tags.map((tag, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3 capitalize">
                {tag.tagCategory}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {tag.associatedTerms.map((term, termIndex) => (
                  <button
                    key={termIndex}
                    onClick={() => handleTermToggle(term)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      selectedTerms.includes(term)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={selectedTerms.length < MIN_SELECTION}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Generate Domains ({selectedTerms.length}/{MIN_SELECTION})
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagSelection; 